<template>
  <div
    ref="rootEl"
    class="candidates-import-manager"
  >
    <div class="card">
      <div class="card-header">
        <button
          type="button"
          class="btn btn-sm btn-primary"
          @click.prevent="onImport"
        >
          Import selected candidates
        </button>
        <a
          v-if="showReturnTo"
          :href="returnToUrl"
          class="btn btn-sm btn-primary"
        >
          {{ returnToTitle }}
        </a>
      </div>
      <div
        v-if="hasMessage"
        class="card-body card-body-alert"
      >
        <div
          class="alert alert-success"
          role="alert"
        >
          {{ message }}
          <button
            type="button"
            class="btn-close float-end"
            aria-label="Close"
            @click.prevent="dismisMessage"
          />
        </div>
      </div>
      <div
        v-if="hasError"
        class="card-body card-body-alert"
      >
        <div
          class="alert alert-danger"
          role="alert"
        >
          {{ errorMessage }}
          <button
            type="button"
            class="btn-close float-end"
            aria-label="Close"
            @click.prevent="dismisErrorMessage"
          />
        </div>
      </div>
      <div
        v-if="isLoading"
        class="card-body"
      >
        <loading-indicator
          :message="loadingMessage"
          :vertical="true"
        />
      </div>
      <div
        v-if="showCandidates"
        class="table-responsive"
      >
        <table class="table table-striped table-bordered table-hover table-sm resources-table import-table">
          <thead>
            <tr>
              <th class="tight">
                <input
                  ref="toggleAll"
                  v-model="importAll"
                  type="checkbox"
                >
              </th>
              <th
                v-if="hasErrorColumn"
                class="tight"
              />
              <th class="tight">#</th>
              <th
                v-for="column in columns || []"
                :key="column.id"
              >
                {{ column.title }}
              </th>
              <th class="tight" />
            </tr>
          </thead>
          <tbody>
            <template
              v-for="(candidate, idx) in candidates"
              :key="candidate.id"
            >
              <candidates-import-row
                :candidate="candidate"
                :idx="idx + 1"
                :columns="columns"
                :has-error-column="hasErrorColumn"
                @edit="showEditCandidateModal"
              />
              <tr
                v-if="showCandidateEvents(candidate)"
                :key="candidateEventKey(candidate)"
                class="events"
              >
                <td />
                <td />
                <td v-if="hasErrorColumn" />
                <td colspan="10">
                  Events:
                  <ul class="my-0">
                    <li
                      v-for="(event_description, eidx) in candidate.attributes.events_description || []"
                      :key="eidx"
                    >
                      {{ event_description.description }}
                      <template v-if="candidateEventHasError(event_description)">
                        <div class="alert alert-danger">{{ event_description.error }}</div>
                      </template>
                    </li>
                  </ul>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
    <teleport
      to="#vue-portal-target"
      append
    >
      <imported-candidate-modal
        ref="candidateModal"
        @success="modalSuccess"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, onBeforeUnmount, onMounted, useTemplateRef } from "vue";
  import { Tooltip } from 'bootstrap'
  import axios from 'axios'
  import Rails from '@rails/ujs'
  import LoadingIndicator from './loading-indicator.vue'
  import ImportedCandidateModal from './imported-candidate-modal.vue'
  import CandidatesImportRow from './candidates-import-row.vue'
  import type { Column, ImportedCandidate, EventDescription} from "./candidates-import-types";

  import { isNonEmptyString } from '../js-utils'

  const props = defineProps({
    listUrl: { default: null, type: String },
    importUrl: { default: null, type: String },
    returnToTitle: { default: null, type: String },
    returnToUrl: { default: null, type: String },
    allowEvents: { default: false, type: Boolean }
  })

  const message = ref<string | null>(null)
  const isLoading = ref<boolean>(false)
  const loadingMessage = ref<string | null>(null)
  const errorMessage = ref<string | null>(null)
  const tableHTML = ref<string | null>(null)
  const columns = ref<Column[]>([])
  const candidates = ref<ImportedCandidate[]>([])
  const hasErrorColumn = ref<boolean>(false)

  const rootEl = useTemplateRef<HTMLElement>("rootEl")
  const candidateModal = useTemplateRef<InstanceType<typeof ImportedCandidateModal>>("candidateModal")

  const hasMessage = computed((): boolean => {
    return isNonEmptyString(message.value)
  })

  const hasError = computed((): boolean => {
    return isNonEmptyString(errorMessage.value)
  })

  const showCandidates = computed((): boolean => {
    return !isLoading.value && Array.isArray(candidates.value) && Array.isArray(columns.value)
  })

  const importAll = computed({
    get (): boolean {
      let checkedCount = 0
      for (const candidate of candidates.value) {
        if (candidate.selected) {
          checkedCount += 1
        }
      }
      return checkedCount > 0 && candidates.value.length === checkedCount
    },
    set (newValue: boolean): void {
      for (const candidate of candidates.value) {
        candidate.selected = newValue
      }
    }
  })

  const showReturnTo = computed((): boolean => {
    return isNonEmptyString(props.returnToTitle)
  })

  let errorTooltips: Tooltip | null = null

  onMounted(() => {
    fetchCandidates()
    if (rootEl.value) {
      errorTooltips = new Tooltip(rootEl.value, {
        selector: '[data-bs-toggle="tooltip"]',
        container: 'body',
        html: true,
        title: (el) => el.getAttribute('data-bs-title') ?? ''
      })
    }
  })

  onBeforeUnmount(() => {
    errorTooltips?.dispose()
    errorTooltips = null
  })

  const fetchCandidates = (): void => {
    if (isLoading.value) {
      return
    }
    // this.isLoading = true
    if (isNonEmptyString(props.listUrl)) {
      loadingMessage.value = 'Loading candidates ...'
      isLoading.value = true

      message.value = null
      errorMessage.value = null

      // preserve candidate selection
      const selectedCandidateIds = [] as string[]
      for (const candidate of candidates.value) {
        if (candidate.selected) {
          selectedCandidateIds.push(candidate.id)
        }
      }

      axios.get(props.listUrl)
        .then((response) => {
          if (typeof response.data.columns === 'object') {
            columns.value = response.data.columns
          } else {
            columns.value = []
          }

          candidates.value = []
          if (Array.isArray(response.data.data)) {
            // this.candidates = response.data.data
            for (const newCandidate of response.data.data) {
              // set selected attribute on candidate
              newCandidate.selected = false
              candidates.value.push(newCandidate)
            }
          }

          isLoading.value = false
          tableHTML.value = response.data.html

          // restore candidate selection and set error state
          updateCandidateState(selectedCandidateIds)
        })
        .catch((error) => {
          errorMessage.value = error.message
          isLoading.value = false
        })
    }
  }

  const onImport = (): void => {
    if (isLoading.value) {
      return
    }

    const selectedIds = []
    for (const candidate of candidates.value) {
      if (candidate.selected) {
        selectedIds.push(candidate.id)
      }
    }

    if (selectedIds.length === 0) {
      errorMessage.value = 'Please select at least one candidate!'
      return
    }

    errorMessage.value = null
    message.value = null
    loadingMessage.value = 'Importing selected candidates ...'
    isLoading.value = true

    const formData = new FormData()
    for (const importId of selectedIds) {
      formData.append('import_ids[]', importId)
    }

    axios.post(props.importUrl, formData, {
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'X-Requested-With': 'XMLHttpRequest',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'X-CSRF-Token': Rails.csrfToken()
      }
    })
      .then((response) => {
        if (typeof response.data.message === 'string') {
          if (response.data.success) {
            message.value = response.data.message
          } else {
            errorMessage.value = response.data.message
          }
        } else {
          errorMessage.value = 'Unknown response from server'
        }
        isLoading.value = false
      })
      .catch((error) => {
        errorMessage.value = error.message
        isLoading.value = false
      })
  }

  const showEditCandidateModal = (candidate: ImportedCandidate): void => {
    candidateModal.value?.showModal(candidate.links.editModal)
  }

  const modalSuccess = (): void => {
    candidateModal.value?.hideModal()
    fetchCandidates() // reload candidates table view
  }

  const updateCandidateState = (selectedCandidateIds: string[]): void => {
    let hasErrors = false
    for (const candidate of candidates.value) {
      candidate.selected = selectedCandidateIds.includes(candidate.id)
      if (Array.isArray(candidate.attributes.error_messages) && candidate.attributes.error_messages.length > 0) {
        hasErrors = true
      }
    }

    hasErrorColumn.value = hasErrors
  }

  const dismisMessage = (): void => {
    message.value = null
  }

  const dismisErrorMessage = (): void => {
    errorMessage.value = null
  }

  const showCandidateEvents = (candidate: ImportedCandidate): boolean =>{
    return props.allowEvents && Array.isArray(candidate.attributes.events_description) && candidate.attributes.events_description.length > 0
  }

  const candidateEventKey = (candidate: ImportedCandidate): string => {
    return `cevents-${candidate.id}`
  }

  const candidateEventHasError = (eventDescription: EventDescription): boolean => {
    return isNonEmptyString(eventDescription.error)
  }
</script>
